// src/components/job-engine/useDashboardCharts.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { getDashboardCharts } from "@/api/jobEngine";
import type { DashboardChartSeries } from "@/api/jobEngine";

const POLL_MS = 15_000;

export interface UseDashboardChartsResult {
  data: DashboardChartSeries | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useDashboardCharts(window_: "1h" | "24h"): UseDashboardChartsResult {
  const [data, setData] = useState<DashboardChartSeries | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setIsLoading(true);
    setError(null);
    getDashboardCharts(window_, ctrl.signal)
      .then((r) => { if (!ctrl.signal.aborted) setData(r); })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (isAxiosError(err)) setError(err.response?.data?.title ?? "Failed to load charts");
        else setError("An unexpected error occurred");
      })
      .finally(() => { if (!ctrl.signal.aborted) setIsLoading(false); });
    return () => ctrl.abort();
  }, [window_, tick]);

  // 15-second poll while mounted
  useEffect(() => {
    const id = window.setInterval(refetch, POLL_MS);
    return () => window.clearInterval(id);
  }, [refetch]);

  return { data, isLoading, error, refetch };
}
